import { useEffect, useRef, useState } from "react"
import { useStore } from "../../stores/store"
import { canvasStore, closeAgent } from "../../stores/canvasStore"
import { useTranslations } from "../../lib/translations"
import { ChatTab } from "./ChatTab"
import { TasksTab } from "./TasksTab"
import { MemoryTab } from "./MemoryTab"
import { SettingsTab } from "./SettingsTab"
import { HistoryTab } from "./HistoryTab"

const TABS = ["chat", "tasks", "memory", "settings", "history"]

export function AgentModal() {
  const t = useTranslations()
  const focusedId = useStore(canvasStore, (s) => s.focusedAgentId)
  const agents    = useStore(canvasStore, (s) => s.agents)
  const agent = focusedId ? agents.find((a) => a.id === focusedId) : null
  const [tab, setTab] = useState("chat")
  const modalRef = useRef(null)

  useEffect(() => {
    if (!agent) return
    setTab("chat")
    const onKey = (e) => { if (e.key === "Escape") closeAgent() }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [agent?.id])

  if (!agent) return null

  // Close when the mousedown lands outside the modal box (not on any
  // descendant). Using onMouseDown — not onClick — avoids closing when
  // a drag-select ends outside the modal. The ref+contains check is
  // robust to React's synthetic event quirks where currentTarget can
  // unexpectedly equal target.
  const onScrimMouseDown = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) closeAgent()
  }

  return (
    <div className="cm-scrim" onMouseDown={onScrimMouseDown}>
      <div className="cm-modal" ref={modalRef}>
        <Side agent={agent} />
        <div className="cm-mmain">
          <div className="cm-tabhead">
            <div className="cm-tabs">
              {TABS.map((k) => (
                <button key={k}
                        type="button"
                        className={`cm-tab ${tab === k ? "on" : ""}`}
                        onClick={() => setTab(k)}>
                  {t(`modal.tabs.${k}`)}
                </button>
              ))}
            </div>
            <div className="cm-x" onClick={closeAgent} title={t("modal.close")}>✕</div>
          </div>
          {tab === "chat"     && <ChatTab agent={agent} />}
          {tab === "tasks"    && <TasksTab agent={agent} />}
          {tab === "memory"   && <MemoryTab agent={agent} />}
          {tab === "settings" && <SettingsTab agent={agent} />}
          {tab === "history"  && <HistoryTab agent={agent} />}
        </div>
      </div>
    </div>
  )
}

function Side({ agent }) {
  const t = useTranslations()
  const statusLabel = agent.status === "active" ? t("modal.status.active")
                    : agent.status === "paused"   ? t("modal.status.paused")
                    : t("modal.status.disabled")
  const dotColor = agent.status === "active" ? "var(--ok)"
                 : agent.status === "paused"   ? "var(--warn)"
                 : "var(--ink-3)"

  return (
    <div className="cm-mside">
      <div>
        <h3>{agent.name}</h3>
        <div className="sub">
          {agent.role}{agent.model ? ` · ${agent.model}` : ""}
        </div>
      </div>
      <div className="cm-statline">
        <span className="d" style={{ background: dotColor }} />{statusLabel}
      </div>
      <div className="meta">
        type → {agent.master ? "master" : agent.manager_id ? "worker" : "independent"}<br />
        canary → pass<br />
        pending approvals → {agent.pending_approvals_count || 0}
      </div>
    </div>
  )
}
